import { useState, useEffect } from 'react';
import { Gift, Copy, Users, DollarSign, TrendingUp, Mail, Share2, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatRegionMoney, getActiveRegionConfig } from '@/lib/regionConfig';
import { toast } from 'sonner';

const getOrCreateReferralCode = () => {
  let code = localStorage.getItem('ixbrl_referral_code');
  if (!code) {
    code = 'REF-' + Math.random().toString(36).substr(2, 8).toUpperCase();
    localStorage.setItem('ixbrl_referral_code', code);
  }
  return code;
};

const Referrals = () => {
  const regionConfig = getActiveRegionConfig();
  const [referralCode] = useState(getOrCreateReferralCode);
  const [referralLink] = useState(() => {
    const params = new URLSearchParams({
      ref: getOrCreateReferralCode(),
      region: regionConfig.code,
      source_site: regionConfig.sourceSite,
    });
    return `https://digitalreporting.co.uk/signup?${params.toString()}`;
  });
  const [email, setEmail] = useState('');
  
  // Mock referral data
  const [stats] = useState({
    totalReferrals: 12,
    successfulReferrals: 8,
    pendingReferrals: 4,
    totalEarnings: 400,
    availableBalance: 400
  });

  const [referrals] = useState([
    { id: 1, name: 'John Smith', email: 'john@example.com', status: 'successful', date: '2024-12-01', earned: 50 },
    { id: 2, name: 'Sarah Wilson', email: 'sarah@example.com', status: 'successful', date: '2024-12-05', earned: 50 },
    { id: 3, name: 'Mike Johnson', email: 'mike@example.com', status: 'pending', date: '2024-12-10', earned: 0 },
    { id: 4, name: 'Emma Davis', email: 'emma@example.com', status: 'pending', date: '2024-12-12', earned: 0 }
  ]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleEmailInvite = () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    toast.success(`Invitation sent to ${email}`);
    setEmail('');
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <p className="text-2xl font-semibold mb-1">{value}</p>
      <p className="text-sm text-muted-foreground">{title}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );

  return (
    <div data-testid="referrals-page">
      <div className="page-header">
        <h1 className="page-title">Referral Program</h1>
        <p className="page-subtitle">Earn {formatRegionMoney(regionConfig.referralReward, regionConfig)} for each successful referral</p>
      </div>

      {/* Referral Hero Section */}
      <div className="bg-primary text-primary-foreground rounded-lg p-8 mb-6">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Invite Friends, Earn Rewards</h2>
          </div>
          <p className="mb-6 opacity-90">
            Share iXBRL Converter with your network and earn {formatRegionMoney(regionConfig.referralReward, regionConfig)} for every friend who signs up and completes their first job.
            Your friends also get 20% off their first conversion!
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-primary-foreground/10 border border-primary-foreground/20 rounded-lg p-4">
              <p className="text-sm opacity-75 mb-2">Your Referral Code</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-lg font-semibold">{referralCode}</code>
                <Button variant="secondary" size="sm" onClick={() => copyToClipboard(referralCode, 'Referral code')}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="bg-primary-foreground/10 border border-primary-foreground/20 rounded-lg p-4">
              <p className="text-sm opacity-75 mb-2">Your Referral Link</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-sm truncate">{referralLink}</code>
                <Button variant="secondary" size="sm" onClick={() => copyToClipboard(referralLink, 'Referral link')}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
        <StatCard 
          icon={Users}
          title="Total Referrals"
          value={stats.totalReferrals}
          color="bg-blue-100 dark:bg-blue-950"
        />
        <StatCard 
          icon={CheckCircle}
          title="Successful"
          value={stats.successfulReferrals}
          subtitle="Converted users"
          color="bg-green-100 dark:bg-green-950"
        />
        <StatCard 
          icon={Clock}
          title="Pending"
          value={stats.pendingReferrals}
          subtitle="Not yet converted"
          color="bg-amber-100 dark:bg-amber-950"
        />
        <StatCard 
          icon={DollarSign}
          title="Total Earnings"
          value={formatRegionMoney(stats.totalEarnings, regionConfig)}
          color="bg-muted"
        />
        <StatCard 
          icon={TrendingUp}
          title="Available Balance"
          value={formatRegionMoney(stats.availableBalance, regionConfig)}
          subtitle="Ready to withdraw"
          color="bg-green-100 dark:bg-green-950"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Share Options */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-6">Share Your Link</h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Invite via Email</label>
              <div className="flex gap-2">
                <Input 
                  type="email" 
                  placeholder="friend@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button onClick={handleEmailInvite}>
                  <Mail className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-sm font-medium mb-3">Share on Social Media</p>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1">
                  <Share2 className="w-4 h-4 mr-2" />
                  Twitter
                </Button>
                <Button variant="outline" className="flex-1">
                  <Share2 className="w-4 h-4 mr-2" />
                  LinkedIn
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Referral List */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-6">Your Referrals</h3>
          
          {referrals.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No referrals yet. Start inviting friends!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral) => (
                    <tr key={referral.id} className="table-row border-b border-border">
                      <td className="py-4 px-4 font-medium text-sm">{referral.name}</td>
                      <td className="py-4 px-4 text-sm text-muted-foreground">{referral.email}</td>
                      <td className="py-4 px-4">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          referral.status === 'successful' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {referral.status === 'successful' ? 'Successful' : 'Pending'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-muted-foreground">{referral.date}</td>
                      <td className="py-4 px-4 text-right font-semibold text-sm">
                        {referral.earned > 0 ? formatRegionMoney(referral.earned, regionConfig) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* How it Works */}
      <div className="mt-6 bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6">How It Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">1</div>
            <div>
              <h4 className="font-medium mb-1">Share Your Link</h4>
              <p className="text-sm text-muted-foreground">Send your unique referral link to friends and colleagues</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">2</div>
            <div>
              <h4 className="font-medium mb-1">They Sign Up</h4>
              <p className="text-sm text-muted-foreground">Your friend creates an account and gets 20% off their first job</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">3</div>
            <div>
              <h4 className="font-medium mb-1">You Earn Rewards</h4>
              <p className="text-sm text-muted-foreground">Get {formatRegionMoney(regionConfig.referralReward, regionConfig)} credited to your account when they complete their first conversion</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
